import {MasterDataLayout, StandardFieldDefinition, ClientGridRendererConfig} from './types';

export const TRANSLATION_SCOPE = 'masterDataModule';
export const TRANSLATION_SEARCH_SCOPE = [TRANSLATION_SCOPE, 'search'];
export const TRANSLATION_DEFAULT_SCOPE = [TRANSLATION_SCOPE, 'default'];
export const TRANSLATION_RENDERER_SCOPE = [TRANSLATION_SCOPE, 'renderer'];

/**
 * Default grid size for master data layout.
 */
export const DEFAULT_GRID_SIZE = {cols: 5, rows: 5};

/**
 * Default master data layout matching the traditional static form appearance.
 * Used as fallback when no custom layout is configured.
 */
export const DEFAULT_MASTER_DATA_LAYOUT: MasterDataLayout = {
  gridSize: {cols: 5, rows: 5},
  positions: [
    {fieldKey: 'gender', fieldType: 'standard', col: 0, row: 0, colSpan: 1, rowSpan: 1},
    {fieldKey: 'firstName', fieldType: 'standard', col: 1, row: 0, colSpan: 2, rowSpan: 1},
    {fieldKey: 'lastName', fieldType: 'standard', col: 3, row: 0, colSpan: 2, rowSpan: 1},
    {fieldKey: 'birthDate', fieldType: 'standard', col: 0, row: 1, colSpan: 1, rowSpan: 1},
    {fieldKey: 'zipCode', fieldType: 'standard', col: 1, row: 1, colSpan: 1, rowSpan: 1},
    {fieldKey: 'city', fieldType: 'standard', col: 2, row: 1, colSpan: 1, rowSpan: 1},
    {fieldKey: 'street', fieldType: 'standard', col: 3, row: 1, colSpan: 1, rowSpan: 1},
    {fieldKey: 'streetNr', fieldType: 'standard', col: 4, row: 1, colSpan: 1, rowSpan: 1},
    {fieldKey: 'address', fieldType: 'standard', col: 0, row: 2, colSpan: 5, rowSpan: 1},
    {fieldKey: 'email', fieldType: 'standard', col: 0, row: 3, colSpan: 2, rowSpan: 1},
    {fieldKey: 'location', fieldType: 'standard', col: 2, row: 3, colSpan: 1, rowSpan: 1},
    {fieldKey: 'status', fieldType: 'standard', col: 3, row: 3, colSpan: 1, rowSpan: 1},
    {fieldKey: 'pseudoId', fieldType: 'standard', col: 4, row: 3, colSpan: 1, rowSpan: 1},
    {fieldKey: 'customerNr', fieldType: 'standard', col: 0, row: 4, colSpan: 1, rowSpan: 1},
    {fieldKey: 'caseNr', fieldType: 'standard', col: 1, row: 4, colSpan: 1, rowSpan: 1},
    {fieldKey: 'locale', fieldType: 'standard', col: 2, row: 4, colSpan: 1, rowSpan: 1},
    {fieldKey: 'allowMail', fieldType: 'standard', col: 3, row: 4, colSpan: 1, rowSpan: 1},
    {fieldKey: 'prefill', fieldType: 'standard', col: 4, row: 4, colSpan: 1, rowSpan: 1}
  ]
};

/**
 * Standard client field definitions with input types and options.
 */
export const STANDARD_FIELD_DEFINITIONS: StandardFieldDefinition[] = [
  {
    key: 'gender',
    labelKey: 'GENDER',
    inputType: 'select',
    required: true,
    options: [
      {value: 'male', labelKey: 'MALE'},
      {value: 'female', labelKey: 'FEMALE'},
      {value: 'diverse', labelKey: 'DIVERSE'}
    ]
  },
  {key: 'firstName', labelKey: 'FIRSTNAME', inputType: 'text', required: true, placeholder: 'ENTER_FIRSTNAME'},
  {key: 'lastName', labelKey: 'LASTNAME', inputType: 'text', required: true, placeholder: 'ENTER_LASTNAME'},
  {key: 'birthDate', labelKey: 'BIRTHDATE', inputType: 'date', required: true},
  {key: 'zipCode', labelKey: 'ZIP_CODE', inputType: 'text'},
  {key: 'city', labelKey: 'CITY', inputType: 'text'},
  {key: 'street', labelKey: 'STREET', inputType: 'text'},
  {key: 'streetNr', labelKey: 'STREET_NR', inputType: 'text'},
  {key: 'address', labelKey: 'ADDRESS_DEPRECATED', inputType: 'text'},
  {key: 'email', labelKey: 'EMAIL', inputType: 'email'},
  {key: 'location', labelKey: 'LOCATION', inputType: 'text', placeholder: 'ENTER_LOCATION'},
  {key: 'pseudoId', labelKey: 'PSEUDO_ID', inputType: 'text', placeholder: 'ENTER_PSEUDO_ID'},
  {key: 'customerNr', labelKey: 'ID', inputType: 'text', required: true, placeholder: 'ENTER_PATIENT_ID'},
  {key: 'caseNr', labelKey: 'CASE_NUMBER', inputType: 'text', placeholder: 'ENTER_CASE_NUMBER'},
  {key: 'status', labelKey: 'STATUS', inputType: 'text', placeholder: 'ENTER_STATUS'},
  {key: 'locale', labelKey: 'LANGUAGE', inputType: 'select'},
  {
    key: 'allowMail',
    labelKey: 'ALLOW_MAIL_OPTION',
    inputType: 'select',
    options: [
      {value: 'true', labelKey: 'YES'},
      {value: 'false', labelKey: 'NO'}
    ]
  },
  {
    key: 'prefill',
    labelKey: 'PREFILL',
    inputType: 'select',
    options: [
      {value: 'yes', labelKey: 'YES'},
      {value: 'no', labelKey: 'NO'},
      {value: 'optional', labelKey: 'OPTIONAL'}
    ]
  }
];

/**
 * Mapping of standard field keys to translation label keys.
 */
export const STANDARD_FIELD_LABEL_KEYS: {[key: string]: string} = {
  gender: 'GENDER',
  firstName: 'FIRSTNAME',
  lastName: 'LASTNAME',
  birthDate: 'BIRTHDATE',
  address: 'ADDRESS_DEPRECATED',
  zipCode: 'ZIP_CODE',
  city: 'CITY',
  street: 'STREET',
  streetNr: 'STREET_NR',
  location: 'LOCATION',
  email: 'EMAIL',
  customerNr: 'ID',
  caseNr: 'CASE_NUMBER',
  pseudoId: 'PSEUDO_ID',
  status: 'STATUS',
  locale: 'LANGUAGE',
  allowMail: 'ALLOW_MAIL_OPTION',
  prefill: 'PREFILL'
};

/**
 * Settings keys for master data layout storage.
 */
export const SETTINGS_KEY_GRID_SIZE = 'masterDataLayout.gridSize';
export const SETTINGS_KEY_POSITIONS = 'masterDataLayout.positions';
export const SETTINGS_KEY_GRID_RENDERER = 'masterDataLayout.gridRenderer';

/**
 * Default grid renderer configuration matching the traditional patient column appearance.
 * Display fields: first name + last name (shown as linked badge).
 * Detail fields: patient id, case number, birthdate, gender (shown as muted text below).
 */
export const DEFAULT_GRID_RENDERER_CONFIG: ClientGridRendererConfig = {
  displayFields: [
    {fieldKey: 'firstName', fieldType: 'standard'},
    {fieldKey: 'lastName', fieldType: 'standard'}
  ],
  detailFields: [
    {fieldKey: 'customerNr', fieldType: 'standard', label: 'Pat. Id'},
    {fieldKey: 'caseNr', fieldType: 'standard', label: 'Fall Nr.'},
    {fieldKey: 'birthDate', fieldType: 'standard', label: '*'},
    {fieldKey: 'gender', fieldType: 'standard'}
  ]
};
